import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { Button, Input } from "reactstrap";
import Swal from "sweetalert2";
import Header from "../../layout/Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { posGet, posPost, avisaError, mensajeError, dinero, hoy, sumaDias, fechaHora, METODOS } from "./posApi";
import { llave, variante } from "./posApi";
import VariantePicker from "./VariantePicker";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import { ticketDevolucion } from "./posTickets";

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const PAGO_VACIO = { efectivo: "", tdc: "", transfer: "", referenciaTdc: "", referenciaTransfer: "" };

function PosDevolucion() {
  const { user } = useContext(AuthContext);
  const puede = !!(user && user.rolTiendas && user.posDevoluciones);
  const ctx = useTienda(false);
  const { tienda } = ctx;

  const [folio, setFolio] = useState("");
  const [venta, setVenta] = useState(null);
  const [devuelve, setDevuelve] = useState({}); // articulo -> cantidad
  const [conCambio, setConCambio] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [resultados, setResultados] = useState([]);
  const [nuevos, setNuevos] = useState([]); // {k, articulos, colores, tallas, color, talla, codigo, nombre, cantidad, existencia}
  const [picker, setPicker] = useState(null);
  const [calculo, setCalculo] = useState(null);
  const [pago, setPago] = useState(PAGO_VACIO);
  const [reembolsoMetodo, setReembolsoMetodo] = useState("efectivo");
  const [motivo, setMotivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [inicio, setInicio] = useState(sumaDias(hoy(), -7));
  const [fin, setFin] = useState(hoy());
  const timer = useRef(null);
  // Vender sin existencia en el cambio: mismo permiso que en caja
  const puedeSinEx = !!(user && user.posVenderSinExistencia);
  const nuevosRef = useRef([]);
  const sinExOk = useRef({});
  useEffect(() => { nuevosRef.current = nuevos; }, [nuevos]);

  const cargarHistorial = useCallback(() => {
    if (!tienda) return;
    posGet("/devoluciones", { area: tienda, inicio, fin }).then(setHistorial).catch(avisaError);
  }, [tienda, inicio, fin]);
  useEffect(() => { cargarHistorial(); }, [cargarHistorial]);

  const limpiar = () => {
    setVenta(null); setDevuelve({}); setConCambio(false); setNuevos([]); setResultados([]);
    setCalculo(null); setPago(PAGO_VACIO); setMotivo(""); setFolio(""); setReembolsoMetodo("efectivo");
  };

  const buscarTicket = async (e) => {
    e.preventDefault();
    if (!folio.trim()) return;
    try {
      const v = await posGet(`/devoluciones/ticket/${encodeURIComponent(folio.trim())}`, { area: tienda });
      if (v.estatus !== "Activa") return Swal.fire({ icon: "warning", title: "Ticket cancelado", text: "No se puede devolver mercancía de un ticket cancelado." });
      setVenta(v); setDevuelve({}); setNuevos([]); setCalculo(null);
    } catch (er) { avisaError(er); }
  };

  const piezasDevueltas = Object.values(devuelve).reduce((t, n) => t + (Number(n) || 0), 0);

  // Recalcula crédito y diferencia en el servidor
  useEffect(() => {
    if (!venta || !piezasDevueltas) { setCalculo(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      posPost("/devoluciones/cotizar", {
        area: tienda, venta: venta._id,
        devuelve: Object.entries(devuelve).filter(([, n]) => Number(n) > 0).map(([k, cantidad]) => { const [articulos, colores, tallas] = k.split("|"); return { articulos, colores: colores || null, tallas: tallas || null, cantidad: Number(cantidad) }; }),
        nuevos: conCambio ? nuevos.map((n) => ({ articulos: n.articulos, colores: n.colores, tallas: n.tallas, cantidad: n.cantidad })) : [],
      }).then(setCalculo).catch((er) => { setCalculo(null); avisaError(er); });
    }, 250);
  }, [venta, devuelve, nuevos, conCambio, tienda, piezasDevueltas]);

  const buscarNuevo = async (e) => {
    e.preventDefault();
    const q = busqueda.trim();
    if (!q) return;
    try {
      const r = await posGet("/articulos", { area: tienda, q });
      if (r.length === 1 && r[0].exacto) return agregarNuevo(r[0]);
      if (!r.length) Swal.fire({ icon: "info", title: "No encontrado", timer: 1500, showConfirmButton: false });
      setResultados(r);
    } catch (er) { avisaError(er); }
  };
  const agregarNuevoVar = async (a, v, cantidad = 1) => {
    const k = llave(a._id, v.colores, v.tallas);
    const ex = ((a.variantes || []).find((x) => llave(a._id, x.colores, x.tallas) === k) || {}).existencia || 0;
    const ya = nuevosRef.current.find((n) => n.k === k);
    if ((ya ? ya.cantidad : 0) + cantidad > ex) {
      if (!puedeSinEx) {
        Swal.fire({ icon: "warning", title: "Sin existencia", text: `${a.codigo} ${variante(v)}: hay ${ex}`, timer: 1800, showConfirmButton: false });
        return;
      }
      if (!sinExOk.current[k]) {
        const r = await Swal.fire({
          icon: "warning", title: "No hay existencia en sistema",
          html: `${a.codigo} ${variante(v)}: el sistema dice que hay <b>${ex}</b>.<br/><br/>¿La tienes en la mano y la entregas de todos modos?<br/><small>El inventario queda en negativo hasta que se dé la entrada.</small>`,
          showCancelButton: true, confirmButtonText: "Sí, entregar", cancelButtonText: "No", focusCancel: true,
        });
        if (!r.value) return;
        sinExOk.current[k] = true;
      }
    }
    setNuevos((prev) => {
      const y = prev.find((n) => n.k === k);
      if (y) return prev.map((n) => (n.k === k ? { ...n, cantidad: n.cantidad + cantidad } : n));
      return [...prev, { k, articulos: a._id, colores: v.colores, tallas: v.tallas, color: v.color, talla: v.talla, codigo: a.codigo, nombre: a.nombre, cantidad, existencia: ex }];
    });
  };
  const agregarNuevo = (a) => {
    setBusqueda(""); setResultados([]);
    if (a.seleccion) { agregarNuevoVar(a, a.seleccion); return; } // escanearon la etiqueta de una talla y color
    if (a.conVariantes) { setPicker(a); return; }
    agregarNuevoVar(a, a.variantes[0] || { colores: null, tallas: null });
  };

  const porCobrar = calculo ? calculo.porCobrar : 0;
  const pagado = r2((Number(pago.efectivo) || 0) + (Number(pago.tdc) || 0) + (Number(pago.transfer) || 0));
  const falta = r2(Math.max(porCobrar - pagado, 0));
  const cambio = r2(Math.max(pagado - porCobrar, 0));

  const registrar = async () => {
    if (!calculo || guardando) return;
    if (porCobrar > 0 && falta > 0) return Swal.fire({ icon: "warning", title: `Falta cobrar ${dinero(falta)}` });
    const texto = calculo.reembolso > 0
      ? `Se le regresan <b>${dinero(calculo.reembolso)}</b> en ${METODOS[reembolsoMetodo].toLowerCase()}.`
      : porCobrar > 0 ? `Se cobran <b>${dinero(porCobrar)}</b> de diferencia.` : "No hay diferencia de dinero.";
    const ok = await Swal.fire({ title: conCambio && nuevos.length ? "¿Registrar el cambio?" : "¿Registrar la devolución?", html: texto, icon: "question", showCancelButton: true, confirmButtonText: "Sí, registrar", cancelButtonText: "Revisar" });
    if (!ok.value) return;
    setGuardando(true);
    try {
      const d = await posPost("/devoluciones", {
        area: tienda, venta: venta._id, motivo, reembolsoMetodo,
        devuelve: Object.entries(devuelve).filter(([, n]) => Number(n) > 0).map(([k, cantidad]) => { const [articulos, colores, tallas] = k.split("|"); return { articulos, colores: colores || null, tallas: tallas || null, cantidad: Number(cantidad) }; }),
        nuevos: conCambio ? nuevos.map((n) => ({ articulos: n.articulos, colores: n.colores, tallas: n.tallas, cantidad: n.cantidad })) : [],
        pagos: pago, sinExistencia: conCambio && nuevos.some((n) => n.cantidad > n.existencia),
      });
      ticketDevolucion(d, venta);
      await Swal.fire({
        icon: "success", title: `${d.tipo} ${d.folio} ${d.tipo === "Cambio" ? "registrado" : "registrada"}`,
        html: d.reembolso && d.reembolso.importe > 0 ? `<div style="font-size:1.4rem;font-weight:800">Regresar: ${dinero(d.reembolso.importe)}</div>` : cambio > 0 ? `<div style="font-size:1.4rem;font-weight:800">Cambio: ${dinero(cambio)}</div>` : "",
      });
      limpiar();
      cargarHistorial();
    } catch (er) {
      Swal.fire({ icon: "error", title: "No se registró", text: mensajeError(er) });
    } finally { setGuardando(false); }
  };

  const reimprimir = async (h) => {
    try {
      const d = await posGet(`/devoluciones/${h._id}`, { area: tienda });
      ticketDevolucion(d, { folio: d.ventaFolio });
    } catch (er) { avisaError(er); }
  };

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-undo mr-2" style={{ color: "#ef4444" }} />Devoluciones y cambios</h3>
            <p className="page-subtitle">Busca el ticket, marca lo que regresa y, si es cambio, agrega lo que se lleva</p>
          </div>
          <div className="page-actions">
            <TiendaSelector ctx={ctx} />
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        {!puede ? (
          <div className="alert alert-warning">Tu usuario no tiene permiso para hacer devoluciones. Pide a un master que lo active.</div>
        ) : !tienda ? (
          <div className="alert alert-warning">Selecciona una tienda.</div>
        ) : !venta ? (
          <form onSubmit={buscarTicket} className="filter-bar">
            <div><label>Folio del ticket</label><Input autoFocus value={folio} onChange={(e) => setFolio(e.target.value.toUpperCase())} placeholder="Como viene en el ticket, ej. T06-266" style={{ fontSize: "1.1rem", width: 300 }} /></div>
            <div><Button color="primary" type="submit"><i className="fas fa-search mr-1" />Buscar ticket</Button></div>
          </form>
        ) : (
          <div className="row">
            <div className="col-lg-8 mb-3">
              <div className="form-section">
                <div className="form-section-header">
                  <i className="fas fa-receipt" />
                  <h4 className="form-section-title">Ticket {venta.folio} · {fechaHora(venta.fecha)} · {dinero(venta.total)}</h4>
                  <Button size="sm" color="secondary" className="ml-auto" onClick={limpiar}>Otro ticket</Button>
                </div>
                <div className="form-section-body">
                  {venta.devoluciones && venta.devoluciones.length > 0 && (
                    <div className="alert alert-info py-2">Este ticket ya tiene {venta.devoluciones.length} devolución(es) por {dinero(venta.devueltoImporte)}.</div>
                  )}
                  <table className="table table-sm">
                    <thead><tr><th>Modelo</th><th className="text-right">Compró</th><th className="text-right">Pagó</th><th className="text-right">Puede devolver</th><th style={{ width: 120 }}>Devuelve</th></tr></thead>
                    <tbody>
                      {venta.renglones.map((r) => {
                        const max = r.cantidad - (r.devuelto || 0);
                        return (
                          <tr key={llave(r.articulos, r.colores, r.tallas)}>
                            <td><strong>{r.codigo}</strong> {r.nombre}{(r.color || r.talla) && <span className="badge badge-light ml-1" style={{ border: "1px solid #e2e8f0" }}>{variante(r)}</span>}{r.promociones && r.promociones.length > 0 && <div>{r.promociones.map((p) => <span key={p} className="badge badge-warning mr-1">{p}</span>)}</div>}</td>
                            <td className="text-right">{r.cantidad}</td>
                            <td className="text-right">{dinero(r.importe)}</td>
                            <td className="text-right">{max}</td>
                            <td>
                              <Input bsSize="sm" type="number" min="0" max={max} disabled={max <= 0} value={devuelve[llave(r.articulos, r.colores, r.tallas)] || ""}
                                onChange={(e) => setDevuelve({ ...devuelve, [llave(r.articulos, r.colores, r.tallas)]: Math.min(Math.max(parseInt(e.target.value, 10) || 0, 0), max) })} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <Input placeholder="Motivo (ej. talla, defecto, no le gustó)" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-header">
                  <i className="fas fa-exchange-alt" />
                  <h4 className="form-section-title">¿Se lleva otra mercancía? (cambio)</h4>
                  <label className="ml-auto mb-0" style={{ fontWeight: 600 }}><input type="checkbox" checked={conCambio} onChange={(e) => setConCambio(e.target.checked)} /> Sí, es cambio</label>
                </div>
                {conCambio && (
                  <div className="form-section-body" style={{ position: "relative" }}>
                    <form onSubmit={buscarNuevo} style={{ display: "flex", gap: 8 }}>
                      <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Escanea o escribe el código de lo que se lleva" />
                      <Button color="primary" type="submit">Agregar</Button>
                    </form>
                    {resultados.length > 0 && (
                      <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, marginTop: 6, maxHeight: 220, overflowY: "auto" }}>
                        {resultados.map((a) => (
                          <button key={a._id} type="button" disabled={a.existencia <= 0 && !puedeSinEx} onClick={() => agregarNuevo(a)} style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "8px 12px", border: "none", borderBottom: "1px solid #f1f5f9", background: "transparent", textAlign: "left", opacity: a.existencia > 0 ? 1 : 0.45 }}>
                            <span><strong>{a.codigo}</strong> · {a.nombre}</span><span>{a.existencia} pzs · <strong>{dinero(a.precioTienda)}</strong></span>
                          </button>
                        ))}
                      </div>
                    )}
                    <table className="table table-sm mt-2">
                      <tbody>
                        {!nuevos.length && <tr><td className="text-muted text-center">Agrega lo que se lleva</td></tr>}
                        {nuevos.map((n) => {
                          const f = calculo && calculo.nueva ? calculo.nueva.items.find((i) => llave(i.articulos, i.colores, i.tallas) === n.k) : null;
                          return (
                            <tr key={n.k}>
                              <td><strong>{n.codigo}</strong> {n.nombre}{(n.color || n.talla) && <span className="badge badge-light ml-1" style={{ border: "1px solid #e2e8f0" }}>{variante(n)}</span>}{f && f.promociones.length > 0 && <span className="badge badge-warning ml-1">{f.promociones.join(", ")}</span>}</td>
                              <td style={{ width: 90 }}><Input bsSize="sm" type="number" min="1" max={puedeSinEx ? undefined : n.existencia} value={n.cantidad} onChange={(e) => setNuevos(nuevos.map((x) => (x.k === n.k ? { ...x, cantidad: Math.max(parseInt(e.target.value, 10) || 1, 1) > x.existencia && !(puedeSinEx && sinExOk.current[x.k]) ? Math.max(x.existencia, 1) : Math.max(parseInt(e.target.value, 10) || 1, 1) } : x)))} /></td>
                              <td className="text-right"><strong>{f ? dinero(f.importe) : "…"}</strong></td>
                              <td style={{ width: 40 }}><Button size="sm" color="danger" onClick={() => setNuevos(nuevos.filter((x) => x.k !== n.k))}><i className="fas fa-times" /></Button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-4 mb-3">
              <div className="form-section" style={{ position: "sticky", top: 10 }}>
                <div className="form-section-header dark"><i className="fas fa-calculator" /><h4 className="form-section-title">Resumen</h4></div>
                <div className="form-section-body">
                  {!calculo ? (
                    <div className="text-muted">Marca cuántas piezas devuelve.</div>
                  ) : (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between" }}><span>Saldo a favor</span><strong>{dinero(calculo.credito)}</strong></div>
                      <div className="small text-muted mb-2">Lo pagado menos lo que vale, con las mismas promociones, lo que se queda.</div>
                      {conCambio && nuevos.length > 0 && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}><span>Se lleva</span><strong>{dinero(calculo.totalNuevo)}</strong></div>
                      )}
                      <hr />
                      {calculo.reembolso > 0 && (
                        <>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.3rem", fontWeight: 800, color: "#dc2626" }}><span>Regresar</span><span>{dinero(calculo.reembolso)}</span></div>
                          <label className="mt-2">¿Cómo se le regresa?</label>
                          <Input type="select" value={reembolsoMetodo} onChange={(e) => setReembolsoMetodo(e.target.value)}>
                            <option value="efectivo">Efectivo de caja</option><option value="tdc">Reverso a tarjeta</option><option value="transfer">Transferencia</option>
                          </Input>
                        </>
                      )}
                      {porCobrar > 0 && (
                        <>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.3rem", fontWeight: 800, color: "#059669" }}><span>Cobrar</span><span>{dinero(porCobrar)}</span></div>
                          {[["efectivo", "Efectivo"], ["tdc", "Tarjeta"], ["transfer", "Transferencia"]].map(([k, l]) => (
                            <div key={k} className="mt-2"><label className="mb-0">{l}</label><Input type="number" min="0" step="0.01" value={pago[k]} onChange={(e) => setPago({ ...pago, [k]: e.target.value })} /></div>
                          ))}
                          <div className="mt-2" style={{ fontWeight: 700, color: falta > 0 ? "#dc2626" : "#059669" }}>{falta > 0 ? `Falta ${dinero(falta)}` : `Cambio ${dinero(cambio)}`}</div>
                        </>
                      )}
                      {calculo.reembolso === 0 && porCobrar === 0 && <div className="text-muted">Sin diferencia de dinero.</div>}
                      <Button color="success" block size="lg" className="mt-3" disabled={guardando || (porCobrar > 0 && falta > 0)} onClick={registrar}>
                        {guardando ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-check mr-1" />Registrar {conCambio && nuevos.length ? "cambio" : "devolución"}</>}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <VariantePicker
          articulo={picker}
          modo="venta"
          permiteSinExistencia={puedeSinEx}
          enCarrito={picker ? Object.fromEntries(nuevos.filter((n) => n.articulos === picker._id).map((n) => [`${n.colores || ""}|${n.tallas || ""}`, n.cantidad])) : {}}
          onAgregar={async (lista) => { for (const v of lista) await agregarNuevoVar(picker, v, v.cantidad); }}
          onCerrar={() => setPicker(null)}
        />

        <div className="form-section mt-2">
          <div className="form-section-header dark"><i className="fas fa-history" /><h4 className="form-section-title">Devoluciones registradas</h4></div>
          <div className="form-section-body">
            <div className="filter-bar">
              <div><label>Desde</label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></div>
              <div><label>Hasta</label><Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></div>
            </div>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead><tr><th>Folio</th><th>Fecha</th><th>Tipo</th><th>Ticket</th><th className="text-right">Piezas</th><th className="text-right">Saldo a favor</th><th className="text-right">Regresado</th><th>Atendió</th><th /></tr></thead>
                <tbody>
                  {!historial.length && <tr><td colSpan={9} className="text-center text-muted">Sin devoluciones en el periodo</td></tr>}
                  {historial.map((h) => (
                    <tr key={h._id}>
                      <td>{h.folio}</td><td>{fechaHora(h.fecha)}</td>
                      <td>{h.tipo === "Cambio" ? <span className="badge badge-info">Cambio{h.ventaNueva ? ` → ${h.ventaNueva.folio}` : ""}</span> : <span className="badge badge-danger">Devolución</span>}</td>
                      <td>{h.ventaFolio}</td><td className="text-right">{h.piezas}</td><td className="text-right">{dinero(h.credito)}</td>
                      <td className="text-right">{h.reembolso && h.reembolso.importe > 0 ? `${dinero(h.reembolso.importe)} ${METODOS[h.reembolso.metodo]}` : ""}</td>
                      <td>{h.user ? h.user.nombre : ""}</td>
                      <td><Button size="sm" color="info" onClick={() => reimprimir(h)} title="Comprobante"><i className="fas fa-print" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PosDevolucion;
